import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import PageShell from './PageShell';
import Seo from '../components/Seo';
import { useCart } from '../context/CartContext';
import { useUserAuth } from '../context/UserAuthContext';
import { useToast } from '../hooks/use-toast';
import { API } from '../api';
import { fmtMoney } from '../utils/media';
import { CreditCard, ShieldCheck, ArrowLeft } from 'lucide-react';

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { user, authHeader } = useUserAuth();
  const { toast } = useToast();
  const nav = useNavigate();
  const [b, setB] = useState({ name: user?.name || '', email: user?.email || '', phone: '', country: 'Türkiye', city: '', address: '' });
  const [loading, setLoading] = useState(false);

  if (!user) return <Navigate to="/login?redirect=/checkout" replace />;
  if (items.length === 0) return <Navigate to="/cart" replace />;

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await API.post('/user/checkout', { items, total, billing: b }, { headers: authHeader() });
      toast({ title: 'Siparişiniz alındı!', description: `Sipariş #${r.data.order_id.slice(0, 8)}— ödeme yönlendirmesi ekibimizden gelecek.` });
      clear();
      nav('/account/orders');
    } catch { toast({ title: 'Hata', description: 'Sipariş oluşturulamadı', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  return (
    <PageShell eyebrow="ÖDEME" title={<><span className="text-white">Siparişi </span><span className="gold-text">Tamamla</span></>} subtitle="Fatura bilgilerinizi girin ve güvenli ödeme ile devam edin.">
      <Seo title="Ödeme" />
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-10">
          <Link to="/cart" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#f4d896] mb-8"><ArrowLeft className="w-4 h-4" /> Sepete dön</Link>
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
            <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-gradient-to-b from-[#111015] to-[#0a090c] p-6 md:p-8 space-y-5">
              <h3 className="font-display text-xl font-bold text-white mb-2">Fatura Bilgileri</h3>
              {[['name','AD SOYAD'],['email','E-POSTA'],['phone','TELEFON'],['country','ÜLKE'],['city','ŞEHİR']].map(([k,l]) => (
                <label key={k} className="block"><span className="text-xs tracking-widest text-white/50">{l}</span>
                  <input required={k!=='phone'} value={b[k]} onChange={(e) => setB({ ...b, [k]: e.target.value })} className="mt-2 w-full h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#e4b44e]/60" /></label>
              ))}
              <label className="block"><span className="text-xs tracking-widest text-white/50">ADRES</span>
                <textarea rows={3} value={b.address} onChange={(e) => setB({ ...b, address: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#e4b44e]/60" /></label>
              <div className="pt-4 border-t border-white/8">
                <h4 className="text-xs tracking-widest text-[#e4b44e]/85 font-bold mb-3">ÖDEME YÖNTEMİ</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-[#e4b44e]/40 bg-[#e4b44e]/5 p-4 flex items-center gap-3"><CreditCard className="w-5 h-5 text-[#f4d896]" /><div><div className="text-sm font-bold text-white">Havale/EFT</div><div className="text-xs text-white/50">Manuel havale bilgileri e-posta ile</div></div></div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-4 flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-white/70" /><div><div className="text-sm font-bold text-white/80">Kredi Kartı</div><div className="text-xs text-white/50">PayTR / İyzico entegrasyonu yakında</div></div></div>
                </div>
              </div>
              <button disabled={loading} className="w-full btn-gold h-12 rounded-full inline-flex items-center justify-center gap-2 text-sm font-extrabold tracking-widest disabled:opacity-60">
                {loading ? 'GÖNDERİLİYOR...' : 'SİPARİŞİ ONAYLA'}
              </button>
            </form>
            <aside className="h-fit lg:sticky lg:top-24 rounded-3xl border border-[#e4b44e]/30 bg-gradient-to-b from-[#1a1710] to-[#0a090c] p-6 md:p-8">
              <h3 className="font-display text-xl font-bold text-white mb-6">Sipariş Özeti</h3>
              <div className="space-y-3 mb-6">
                {items.map((it) => (
                  <div key={it.product_id} className="flex justify-between text-sm">
                    <span className="text-white/70 truncate mr-2">{it.name} <span className="text-white/40">×{it.quantity}</span></span>
                    <span className="text-white whitespace-nowrap">{fmtMoney(it.price * it.quantity)} ₺</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 pt-4 flex justify-between items-baseline">
                <span className="font-display text-white font-bold">TOPLAM</span>
                <span className="font-display text-3xl font-black gold-text">{fmtMoney(total)} ₺</span>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
